<script setup lang="ts">
import { computed, onBeforeMount, reactive, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import { useToast } from "vue-toast-notification";

import { useDebounce } from "@/composables/useDebounce";
import { isset } from "@/helpers/object";
import { concat } from "@/helpers/string";
import { mappedWorkerToLoadedWorker } from "@/modules/activeProposal/helpers/formatters";
import { useAuthStore } from "@/modules/auth/store/authStore";
import paymentRequestService from "@/modules/paymentRequest/services/paymentRequestService";
import { type PaymentRequestCreateBody, PaymentRequestCreatedOrigin } from "@/modules/paymentRequest/types";
import workerService from "@/modules/worker/services/workerService";
import { FetchWorkersSort } from "@/modules/worker/types";
import { SortOrder } from '@/types/common';

import type { LoadedWorker } from "@/components/dropdown/types";

import Alert from "@/dsComponents/Alert/Alert.vue";
import { AvatarSize } from "@/dsComponents/Avatar";
import Avatar from "@/dsComponents/Avatar/Avatar.vue";
import Button from "@/dsComponents/buttons/Button.vue";
import ComboButton from "@/dsComponents/buttons/ComboButton.vue";
import Link from "@/dsComponents/buttons/Link.vue";
import TextInputAlt from "@/dsComponents/inputs/TextInputAlt.vue";
import Modal from "@/dsComponents/modals/Modal.vue";
import ToggleLabel from "@/dsComponents/Toggle/ToggleLabel.vue";

import WorkerDropdown from "@/components/dropdown/WorkerDropdown.vue";

interface PaymentRequestCreationModalWorker {
    workerId: string;
    firstname: string;
    lastname: string;
    picture?: string;
}

interface PaymentRequestCreationModalEditTarget {
    id: string;
    requestedAmount: number;
    worker: PaymentRequestCreationModalWorker;
}

interface PaymentRequestCreationModalProps {
    open: boolean;
    worker?: PaymentRequestCreationModalWorker | null;
    paymentRequest?: PaymentRequestCreationModalEditTarget | null;
}

const CHUNK = 50;

const props = defineProps<PaymentRequestCreationModalProps>();

const isEditing = computed<boolean>(() => Boolean(props.paymentRequest));

const emit = defineEmits<{
    // biome-ignore lint/suspicious/noExplicitAny: Init Biome
    (e: "submit", amount: number, workerId: string, result: any): void;
    (e: "close"): void;
}>();

const { t } = useI18n();
const authStore = useAuthStore();
const toaster = useToast();

const loading = ref(false);
const errors = reactive({
    noCandidateSelected: false,
    noAmountSelected: false,
});
const amount = ref("");
const maxAmount = ref(false);
const selectedProfileRef = ref<null | PaymentRequestCreationModalWorker>(null);
const selectedProfile = computed<null | PaymentRequestCreationModalWorker>({
    get: () => props.paymentRequest?.worker ?? props.worker ?? selectedProfileRef.value ?? null,
    set: (val) => {
        selectedProfileRef.value = val;
    },
});

const lockedWorker = computed<null | PaymentRequestCreationModalWorker>(() => props.paymentRequest?.worker ?? props.worker ?? null);
const workerDropdownState = reactive({
    search: "",
    page: 1,
    workers: [] as LoadedWorker[],
    totalItems: 0,
    loading: false,
});

const handleSavePaymentRequest = async () => {
    let requestedAmount = 0;
    if (amount.value && !maxAmount.value) {
        // biome-ignore lint/style/noNonNullAssertion: Init Biome
        requestedAmount = Number(amount.value.match(/\d+/)![0]);
    }

    if (!selectedProfile.value) {
        errors.noCandidateSelected = true;
    }
    if (requestedAmount === 0 && !maxAmount.value) {
        errors.noAmountSelected = true;
    }
    if (Object.values(errors).some((error) => error)) return;

    try {
        loading.value = true;

        if (isEditing.value && props.paymentRequest) {
            await paymentRequestService.updateRequestedAmount({
                paymentRequestId: props.paymentRequest.id,
                requestedAmount,
            });

            toaster?.open({
                message: t("page.paymentRequest.updateSuccess"),
                type: "success",
                duration: 1000,
            });
            emit("submit", requestedAmount, selectedProfile.value?.workerId as string, null);
            return;
        }

        const body: PaymentRequestCreateBody = {
            origin: PaymentRequestCreatedOrigin.AGENCY,
            requestedAmount,
            agencyId: authStore.currentUser?.agencyId ?? "",
            workerId: selectedProfile.value?.workerId ?? "",
        };

        const result = await paymentRequestService.createPaymentRequest({ body });

        toaster?.open({
            message: t("page.paymentRequest.createSuccess"),
            type: "success",
            duration: 1000,
        });
        emit("submit", requestedAmount, selectedProfile.value?.workerId as string, result);
    // biome-ignore lint/suspicious/noExplicitAny: Init Biome
    } catch (e: any) {
        if (e.response?.data?.code === "PAYMENT_REQUEST.ALREADY_EXISTS") {
            toaster?.open({
                message: t("page.paymentRequest.creationModal.errors.alreadyExists"),
                type: "error",
                duration: 10000,
            });
            return;
        }

        console.error(e);
        toaster?.open({
            message: t("errors.Global.ServerError._default"),
            type: "error",
            duration: 10000,
        });
    } finally {
        loading.value = false;
    }
};

const handleMaxAmount = () => {
    errors.noAmountSelected = false;
    maxAmount.value = !maxAmount.value;
    if (maxAmount.value) {
        amount.value = t("page.paymentRequest.max");
    } else {
        amount.value = "";
    }
};

const sortWorkers = (a: LoadedWorker, b: LoadedWorker) => {
    const aName = concat([a.firstname, a.lastname]).toLowerCase();
    const bName = concat([b.firstname, b.lastname]).toLowerCase();
    return aName.localeCompare(bName);
};

const fetchWorkers = async (replace = false) => {
    workerDropdownState.loading = true;
    try {
        const data = await workerService.loadWorkers({
            page: workerDropdownState.page,
            limit: CHUNK,
            sort: FetchWorkersSort.NAME,
            order: SortOrder.ASC,
            search: workerDropdownState.search.trim(),
        });
        const mapped = (data?.items ?? []).map(mappedWorkerToLoadedWorker).filter((w) => isset(concat([w.firstname, w.lastname])));

        if (replace) {
            workerDropdownState.workers = mapped.sort(sortWorkers);
        } else {
            const existingIds = new Set(workerDropdownState.workers.map((w) => w.id));
            workerDropdownState.workers = workerDropdownState.workers.concat(mapped.filter((w) => !existingIds.has(w.id))).sort(sortWorkers);
        }
        workerDropdownState.totalItems = data?.total ?? 0;
    } catch (e) {
        console.error(e);
        toaster?.open({ message: t("errors.worker.loadError"), type: "error" });
    } finally {
        workerDropdownState.loading = false;
    }
};

const debouncedSearchWorkers = useDebounce(() => fetchWorkers(true), 500);

const handleWorkerSearchChange = (search: string) => {
    if (search === workerDropdownState.search) return;
    workerDropdownState.search = search;
    workerDropdownState.page = 1;
    debouncedSearchWorkers();
};

const handleWorkerSelect = (id: string) => {
    errors.noCandidateSelected = false;
    const found = workerDropdownState.workers.find((worker) => worker.id === id);
    if (found) {
        selectedProfile.value = { firstname: found.firstname, lastname: found.lastname, workerId: found.id, picture: found.picture };
    }
};

const drodpownNextPage = () => {
    if (workerDropdownState.workers.length >= workerDropdownState.totalItems) return;
    workerDropdownState.page++;
    fetchWorkers();
};

const prefillAmountFromPaymentRequest = () => {
    if (!props.paymentRequest) return;
    const value = props.paymentRequest.requestedAmount;
    if (value > 0) {
        amount.value = `${value} €`;
        maxAmount.value = false;
    } else {
        amount.value = t("page.paymentRequest.max");
        maxAmount.value = true;
    }
};

onBeforeMount(async () => {
    if (!lockedWorker.value) await fetchWorkers(true);
    prefillAmountFromPaymentRequest();
});

watch(
    () => props.open,
    (open) => {
        if (open && !lockedWorker.value && workerDropdownState.workers.length === 0) fetchWorkers(true);
        if (open) prefillAmountFromPaymentRequest();
    }
);

watch(
    () => props.paymentRequest?.id,
    () => prefillAmountFromPaymentRequest()
);
</script>

<template>
    <div class="payment-request-creation-modal">
        <Modal
            :open="open"
            position="top"
            width="500px"
            :no-padding="true"
            :title="isEditing ? t('page.paymentRequest.editPaymentRequest') : t('page.paymentRequest.newPaymentRequest')"
            :close="!loading"
            @close="emit('close')"
        >
            <template #default>
                <div class="payment-request-form">
                    <div class="modal-section-container">
                        <div class="interim-container">
                            <div class="row-label">
                                <span :class="['field-name', { 'field-error': errors.noCandidateSelected }]">{{ t("form.interim") }}</span>
                                <span v-if="!lockedWorker" class="field-mandatory">*</span>
                            </div>
                            <div class="row-content">
                                <WorkerDropdown
                                    v-if="!lockedWorker"
                                    :model-value="workerDropdownState.search"
                                    :loading="workerDropdownState.loading"
                                    :workers="workerDropdownState.workers"
                                    :totalItems="workerDropdownState.totalItems"
                                    @more="drodpownNextPage"
                                    @update:modelValue="handleWorkerSearchChange"
                                    @select="handleWorkerSelect"
                                >
                                    <template #trigger-zone>
                                        <ComboButton
                                            v-if="!selectedProfile"
                                            variant="ghost"
                                            icon-left="user-circle"
                                            :label="t('page.paymentRequest.creationModal.choseWorker')"
                                            :error="errors.noCandidateSelected"
                                            placeholder
                                        />
                                        <ComboButton v-else variant="ghost">
                                            <div class="selected-profile">
                                                <Avatar
                                                    :firstname="selectedProfile.firstname"
                                                    :lastname="selectedProfile.lastname"
                                                    :size="AvatarSize.size14"
                                                    :image-url="selectedProfile.picture"
                                                />
                                                <span class="profile-label">{{ selectedProfile.firstname }} {{ selectedProfile.lastname }}</span>
                                            </div>
                                        </ComboButton>
                                    </template>
                                </WorkerDropdown>
                                <ComboButton v-else-if="lockedWorker" variant="ghost" readonly>
                                    <div class="selected-profile">
                                        <Avatar
                                            :firstname="lockedWorker.firstname"
                                            :lastname="lockedWorker.lastname"
                                            :size="AvatarSize.size14"
                                            :image-url="lockedWorker.picture"
                                        />
                                        <span class="profile-label">{{ lockedWorker.firstname }} {{ lockedWorker.lastname }}</span>
                                    </div>
                                </ComboButton>
                            </div>
                        </div>
                        <Alert
                            v-if="errors.noCandidateSelected"
                            :message="t('page.paymentRequest.creationModal.errors.noCandidateSelected')"
                            variant="error"
                        />
                    </div>
                    <div class="section-separator">
                        <span class="separator-label">{{ t("page.paymentRequest.amount") }}</span>
                        <div class="content-divider" />
                    </div>
                    <div class="modal-section-container">
                        <div class="payment-amount-container">
                            <TextInputAlt
                                v-model="amount"
                                size="lg"
                                placeholder="0 €"
                                type="currency"
                                @input="errors.noAmountSelected = false"
                                variant="grey"
                                :disabled="maxAmount"
                            />
                            <ToggleLabel :active="maxAmount" :label="t('page.paymentRequest.maxAmount')" size="sm" place @input="handleMaxAmount" />
                        </div>
                        <Alert
                            v-if="errors.noAmountSelected"
                            :message="t('page.paymentRequest.creationModal.errors.noAmountSelected')"
                            variant="error"
                        />
                    </div>
                </div>
            </template>
            <template #footer>
                <div class="button-container">
                    <Link size="md" :label="t('global.actions.cancel')" type="neutral" @click="emit('close')" :disabled="loading" />
                    <Button :label="t('global.actions.save')" variant="filled" size="sm" @click="handleSavePaymentRequest" :loading="loading" />
                </div>
            </template>
        </Modal>
    </div>
</template>

<style scoped lang="scss">
.payment-request-form {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 24px;
    align-self: stretch;
    padding: 24px;

    .modal-section-container {
        display: flex;
        flex-direction: column;
        gap: 8px;
        width: 100%;
    }

    .interim-container {
        display: grid;
        grid-template-rows: 1fr;
        grid-template-columns: 100px 1fr;
        width: 100%;
        .row-label {
            display: flex;
            align-items: center;
            gap: 4px;
            @include font-size-12;
            font-weight: 500;
            line-height: 150%;
            .field-name {
                color: $text-sub-600;
                &.field-error {
                    color: $red-base;
                }
            }
            .field-mandatory {
                color: $blue-base;
            }
        }
    }

    .payment-amount-container {
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        gap: 8px;
        align-self: stretch;
    }
}

.section-separator {
    display: flex;
    padding-top: 8px;
    align-items: center;
    gap: 12px;
    align-self: stretch;
    .content-divider {
        position: relative;
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        flex: 1 0 0;
        .separator-label {
            color: $text-main-900;
            @include font-size-14;
            font-weight: 500;
            line-height: 20px;
        }
        &::before {
            content: "";
            height: 1px;
            background-color: $bg-soft-200;
            width: 100%;
            top: 50%;
            left: 0;
        }
    }
}

.button-container {
    display: flex;
    gap: 24px;
    align-items: center;
    justify-content: end;
}

:deep(.selected-profile) {
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 4px;
}
</style>
